import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Select, { components as RSComponents } from "react-select";
import CreatableSelect from "react-select/creatable";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import Sidenav from "../components/Sidenav";
import RemarksForm from "../components/RemarksForm";
import CRMFilterModal from "../components/CRMFilterModal";
import DeleteDocumentModal from "../components/DeleteDocumentModal";
import "../Console.css";

/* ------------------ Helpers & theme ------------------ */
function fmtDate(dt) {
  if (!dt) return "";
  return new Date(dt).toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

const lightSelectStyles = {
  control: (s) => ({
    ...s,
    backgroundColor: "#fff",
    borderColor: "#e5e7eb",
    minHeight: 38,
    color: "#111827",
  }),
  menu: (s) => ({
    ...s,
    backgroundColor: "#fff",
    zIndex: 9999,
    color: "#111827",
  }),
  option: (s, { isFocused, isSelected }) => ({
    ...s,
    backgroundColor: isSelected ? "rgba(255,81,0,0.1)" : isFocused ? "#f3f4f6" : "#fff",
    color: isSelected ? "#c13d00" : "#111827",
  }),
  multiValue: (s) => ({
    ...s,
    backgroundColor: "rgba(255,81,0,0.1)",
    border: "1px solid rgba(255,81,0,0.2)",
    borderRadius: 6,
  }),
  multiValueLabel: (s) => ({ ...s, color: "#c13d00", fontWeight: 600 }),
  multiValueRemove: (s) => ({
    ...s,
    color: "#c13d00",
    ":hover": { backgroundColor: "rgba(255,81,0,0.2)", color: "#c13d00" },
  }),
  placeholder: (s) => ({ ...s, color: "#6b7280" }),
  input: (s) => ({ ...s, color: "#111827" }),
  singleValue: (s) => ({ ...s, color: "#111827" }),
  indicatorSeparator: () => ({ display: "none" }),
};

/* ----- Colored dot helpers for Status/Priority ----- */
const dotStyle = (color) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: color || "#d1d5db",
  display: "inline-block",
  flex: "0 0 10px",
});

const Dot = ({ color, mr = 0 }) => (
  <span style={{ ...dotStyle(color), marginRight: mr }} />
);

const selectWithDots = {
  Option: (props) => (
    <RSComponents.Option {...props}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <Dot color={props.data.color} />
        {props.label}
      </span>
    </RSComponents.Option>
  ),
  SingleValue: (props) => (
    <RSComponents.SingleValue {...props}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <Dot color={props.data.color} />
        {props.data.label}
      </span>
    </RSComponents.SingleValue>
  ),
};

/* ------------------ Component ------------------ */
const Dashboard = ({ domain, userData }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [userDataState, setUserDataState] = useState(userData);
  const [
    userId,
    userType,
    userName,
    userEmail,
    userPassword,
    userOrg,
    userPermissions,
    userAnonymous,
  ] = userDataState || [];

  const [userOrganization, setUserOrganization] = useState("");
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchRef, setSearchRef] = useState("");

  // pagination (server-side)
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [totalPosts, setTotalPosts] = useState(null); // null => unknown
  const [totalPages, setTotalPages] = useState(null); // null => unknown

  // Modals
  const [showRemarksFormModal, setShowRemarksFormModal] = useState(false);
  const [currentRemarks, setCurrentRemarks] = useState(null);
  const [currentPost, setCurrentPost] = useState(null);
  const [showCRMFilterModal, setShowCRMFilterModal] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [deleteDocTarget, setDeleteDocTarget] = useState(null);

  // Role helpers
  const isAdminUser = userType === "crmmanager" || userType === "superadmin";
  const isBrandAdmin = userType === "brandadmin";

  useEffect(() => {
    setUserDataState(userData);
  }, [userData]);

  useEffect(() => {
    const me = accounts.find(
      (a) => a._id === (userDataState && userDataState[0]),
    );
    if (me) setUserOrganization(me.organization);
  }, [accounts, userDataState]);

  const adminEnabled =
    userOrganization === "Greenbridge" || userOrganization === "ACN";

  const statuses = useMemo(
    () => [
      { value: "Under review", label: "Under review", color: "#f59e0b" },
      { value: "Escalated", label: "Escalated", color: "#ef4444" },
      {
        value: "Awaiting feedback",
        label: "Awaiting feedback",
        color: "#facc15",
      },
      { value: "Resolved", label: "Resolved", color: "#16a34a" },
    ],
    [],
  );

  const priorities = useMemo(
    () => [
      { value: "High", label: "High", color: "#ef4444" },
      { value: "Medium", label: "Medium", color: "#f59e0b" },
      { value: "Low", label: "Low", color: "#0f766e" },
      { value: "Spam", label: "Spam", color: "#6b7280" },
    ],
    [],
  );

  /* ------------------ API ------------------ */
  const fetchTags = () => {
    fetch("/api/tickets/tags")
      .then((r) => r.json())
      .then((data) => setTags(data || []))
      .catch((e) => console.error("Error fetching tags:", e));
  };

  const fetchAccounts = () => {
    fetch("/api/users/admins")
      .then((r) => r.json())
      .then((data) => setAccounts(data || []))
      .catch((e) => console.error("Error fetching accounts:", e));
  };

  const autoAssignTickets = () => {
    fetch(`/api/tickets/autoassign?agent=${userId}`)
      .then((r) => r.json())
      .then(() => fetchPosts(page))
      .catch((e) => console.error(e));
  };

  const reqSeqRef = useRef(0);
  const fetchControllerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/") return;
      const tag = document.activeElement?.tagName?.toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (showRemarksFormModal || showCRMFilterModal || showDeleteDocModal) return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showRemarksFormModal, showCRMFilterModal, showDeleteDocModal]);

  const fetchPosts = (pageNumber = 1) => {
    // Non-admins must have a userId before querying (prevents empty/stale requests)
    if (!isAdminUser && !userId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    fetchControllerRef.current = controller;
    const reqId = ++reqSeqRef.current;

    setIsLoading(true);

    const adminView = isAdminUser;
    const q = new URLSearchParams();
    q.set("page", pageNumber);
    q.set("size", pageSize);
    if (searchRef) q.set("postId", searchRef);
    if (!adminView && userId) q.set("assignee", userId);

    fetch(`/api/tickets/postTickets?${q.toString()}`, {
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.statusText))))
      .then((data) => {
        if (reqId !== reqSeqRef.current) return; // ignore stale

        setPosts(data.posts || []);

        const total =
          data.total ??
          data.totalCount ??
          data.count ??
          data.totalPosts ??
          null;
        if (Number.isFinite(total)) {
          setTotalPosts(total);
          setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
        } else {
          setTotalPosts(null);
          setTotalPages(null);
        }

        fetchTags();
        fetchAccounts();
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          console.error("Error fetching posts:", e);
        }
      })
      .finally(() => {
        if (reqId === reqSeqRef.current) setIsLoading(false);
      });
  };

  // initial & role-change load
  useEffect(() => {
    if (isAdminUser || userId) {
      setPage(1);
      fetchPosts(1);
      if (userType === "backoffice" && userId) autoAssignTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, userId, isAdminUser]);

  // on route change, refresh page 1
  useEffect(() => {
    if (isAdminUser || userId) {
      setPage(1);
      fetchPosts(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, isAdminUser, userId]);

  const patch = (url, body) =>
    fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

  const postJSON = (url, body) =>
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

  /* ---------- Optimistic handlers (no refetch flicker) ---------- */
  const handleStatusAPI = (statusValue, postId) => {
    setPosts((prev) =>
      prev.map((p) =>
        p._id === postId
          ? {
              ...p,
              ticket: {
                ...p.ticket,
                status: [
                  ...(p.ticket?.status || []),
                  { status: statusValue, user: userId },
                ],
              },
            }
          : p,
      ),
    );
    return patch(`/api/tickets/status/${postId}`, {
      status: [{ status: statusValue, user: userId }],
    }).catch((err) => {
      console.error(err);
      fetchPosts(page);
    });
  };

  const handlePriorityAPI = (priorityValue, postId) => {
    setPosts((prev) =>
      prev.map((p) =>
        p._id === postId
          ? { ...p, ticket: { ...p.ticket, priority: priorityValue } }
          : p,
      ),
    );
    return patch(`/api/tickets/${postId}`, { priority: priorityValue }).catch(
      (err) => {
        console.error(err);
        fetchPosts(page);
      },
    );
  };

  const handleTagsAPI = (tagsArray, postId) => {
    setPosts((prev) =>
      prev.map((p) =>
        p._id === postId
          ? { ...p, ticket: { ...p.ticket, tags: tagsArray } }
          : p,
      ),
    );
    return patch(`/api/tickets/${postId}`, { tags: tagsArray }).catch((err) => {
      console.error(err);
      fetchPosts(page);
    });
  };

  const handleAssigneesAPI = (assigneeIds, postId) => {
    const assignees = assigneeIds.map((id) => ({ user: id }));
    setPosts((prev) =>
      prev.map((p) =>
        p._id === postId ? { ...p, ticket: { ...p.ticket, assignees } } : p,
      ),
    );
    return patch(`/api/tickets/${postId}`, { assignees }).catch((err) => {
      console.error(err);
      fetchPosts(page);
    });
  };

  const handleUploadAPI = (file, postId) => {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("user", userDataState[0]);
    return fetch(`/api/tickets/${postId}/document`, {
      method: "POST",
      body: formData,
    }).then(() => fetchPosts(page));
  };

  const handleDeleteDocAPI = (postId, docId) =>
    fetch(`/api/tickets/${postId}/document/${docId}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user: userDataState[0] }),
    }).then(() => fetchPosts(page));

  const handleDocDownload = async (url, filename) => {
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Network error");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback: open in new tab if direct download fails
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };
  const handleRemarksAPI = (postId, comment) =>
    postJSON(`/api/tickets/${postId}/remarks`, {
      comment,
      user: userDataState[0],
    }).then(() => fetchPosts(page));

  /* ------------------ Derived UI helpers ------------------ */
  // Quick lookups
  const accountById = useMemo(() => {
    const map = new Map();
    for (const a of accounts || []) map.set(a._id, a);
    return map;
  }, [accounts]);

  // Build all groups for accounts (for admin/superadmin)
  const groupedAccountsAll = useMemo(() => {
    const grouped = (accounts || []).reduce((acc, acct) => {
      const k = (acct.organizationType || "Other").trim();
      (acc[k] ||= []).push({
        value: acct._id,
        label: `${acct.email} (${acct.organization})`,
      });
      return acc;
    }, {});
    return Object.entries(grouped).map(([label, options]) => ({
      label,
      options,
    }));
  }, [accounts]);

  // For brandadmin: only show "enforcement" and "judiciary"
  const allowedTypes = useMemo(() => new Set(["enforcement", "judiciary"]), []);
  const escalateOptions = useMemo(() => {
    if (!isBrandAdmin) return groupedAccountsAll;
    return groupedAccountsAll.filter((g) =>
      allowedTypes.has(String(g.label || "").toLowerCase()),
    );
  }, [groupedAccountsAll, isBrandAdmin, allowedTypes]);

  // Precompute allowed IDs set for brandadmin (used to filter visual value AND to build union on change)
  const allowedAssigneeIdSet = useMemo(() => {
    if (!isBrandAdmin) return null;
    const set = new Set(
      (accounts || [])
        .filter((a) =>
          allowedTypes.has(String(a.organizationType || "").toLowerCase()),
        )
        .map((a) => a._id),
    );
    return set;
  }, [accounts, isBrandAdmin, allowedTypes]);

  const statusMetaFor = (status) =>
    statuses.find((s) => s.value === status) || null;

  const isLastPage =
    totalPages != null ? page >= totalPages : posts.length < pageSize;

  /* ------------------ Pagination rendering ------------------ */
  const pageNumbers = useMemo(() => {
    if (!totalPages || totalPages <= 1) return [page]; // unknown totals or single page
    const nums = [];
    const add = (n) => nums.push(n);

    const windowSize = 1; // show current ±1
    const start = Math.max(2, page - windowSize);
    const end = Math.min(totalPages - 1, page + windowSize);

    add(1);
    if (start > 2) add("…");
    for (let n = start; n <= end; n++) add(n);
    if (end < totalPages - 1) add("…");
    if (totalPages > 1) add(totalPages);
    return nums;
  }, [page, totalPages]);

  const goToPage = (p) => {
    if (p < 1) p = 1;
    if (totalPages != null) p = Math.min(p, totalPages);
    setPage(p);
    fetchPosts(p);
  };

  /* ------------------ Styles ------------------ */
  const headerStyleFix = (
    <style>{`
      .card-grid { display:grid; grid-template-columns: 1fr; gap: 12px; }
      @media (min-width: 900px) {
        .card-grid { grid-template-columns: 1fr 1fr; }
      }
      @media (min-width: 1300px) {
        .card-grid { grid-template-columns: 1fr 1fr 1fr; }
      }
      .ticket-card {
        background:#fff; border:1px solid #e5e7eb; border-radius:12px;
        padding:16px; box-shadow:0 1px 2px rgba(0,0,0,0.03), 0 8px 24px rgba(0,0,0,0.04);
        color:#111827;
      }
      .ticket-meta { display:flex; flex-wrap:wrap; gap:8px 16px; font-size:12px; color:#374151; }
      .ticket-controls { display:grid; gap:10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
      .ticket-desc { color:#111827; font-size:14px; line-height:1.35; }
      .pill { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:9999px; background:#f3f4f6; color:#111827; font-size:12px; }
      .pill-link { text-decoration:none; color:#111827; transition: background .15s ease; }
      .pill-link:hover { background:#eef2ff; }
      .toolbar { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
      .pager { display:flex; align-items:center; gap:8px; }
      .btn-light { background:#fff; border:1px solid #e5e7eb; border-radius:10px; padding:8px 12px; color:#111827; }
      .btn-ghost { background:transparent; border:1px solid #e5e7eb; border-radius:10px; padding:8px 12px; color:#111827; }
      .btn-ghost[disabled], .btn-light[disabled] { opacity:0.6; cursor:not-allowed; }
      .file-list { padding:0; margin:0; list-style:none; }
      .file-item { display:flex; align-items:center; gap:8px; margin-bottom:6px; padding:4px 8px; border-radius:8px; background:#f9fafb; }
      .file-link { text-decoration:none; color:#2563eb; flex:1; }
      .page-btn { border:1px solid #e5e7eb; background:#fff; color:#000000; padding:6px 10px; border-radius:8px; cursor:pointer; }
      .page-btn.active { background:#111827; color:#fff; border-color:#111827; }
      .page-btn[disabled] { opacity:.6; cursor:not-allowed; }
      .gap-12 { gap:12px; }

      /* Tag pills (read-only) */
      .tag-pill {
        display:inline-flex; align-items:center; gap:6px;
        background:#eef2ff; color:#1f2937; border-radius:9999px; padding:4px 10px;
        font-size:12px; font-weight:700; margin:2px;
      }

      /* Documents grid */
      .doc-grid {
        display:grid; gap:10px;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      }
      .doc-card {
        display:flex; align-items:center; gap:10px;
        background:#f9fafb; border:1px solid #e5e7eb; border-radius:10px;
        padding:8px 10px;
      }
      .doc-icon {
        width:34px; height:34px; border-radius:8px; display:flex; align-items:center; justify-content:center;
        background:#fff; border:1px solid #e5e7eb;
      }
      .doc-name {
        flex:1; min-width:0;
      }
      .doc-name a {
        color:#2563eb; text-decoration:none; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; display:block;
      }
      .doc-actions {
        display:flex; align-items:center; gap:6px;
      }
      .btn-icon {
        background:#fff; border:1px solid #e5e7eb; border-radius:8px; padding:6px 8px; color:#111827;
      }
      .btn-icon:hover { background:#f3f4f6; }
    `}</style>
  );

  /* ------------------ Layout ------------------ */
  return (
    <>
      <Helmet>
        <title>BLEEP CRM</title>
        <meta
          name="description"
          content="BLEEP CRM — review and triage fast."
        />
      </Helmet>

      {headerStyleFix}

      <div className="d-flex">
        <Sidenav domain={domain} />

        {/* Scrollable content */}
        <div
          style={{
            position: "relative",
            height: "calc(100vh - 94px)",
            overflowY: "auto",
            width: "100%",
          }}
        >
          <header
            style={{
              padding: "28px 30px 24px",
              background: "#fff",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div>
                <h1
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: "#111827",
                    margin: 0,
                    lineHeight: 1.2,
                  }}
                >
                  {(() => {
                    const h = new Date().getHours();
                    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
                  })()}, {userName || "Admin"}
                </h1>
                <p
                  style={{
                    color: "#6b7280",
                    fontSize: 14,
                    marginTop: 6,
                    marginBottom: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Bleeps assigned to you for review are listed below.
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 4,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
                {totalPosts != null && (
                  <div style={{ fontSize: 12, color: "#9ca3af" }}>
                    {totalPosts.toLocaleString()} ticket{totalPosts !== 1 ? "s" : ""} total
                  </div>
                )}
              </div>
            </div>
          </header>

          <section style={{ padding: 20 }}>
            <div
              style={{
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 16,
                boxShadow:
                  "0 1px 2px rgba(0,0,0,0.03), 0 8px 24px rgba(0,0,0,0.04)",
              }}
            >
              {isLoading && posts.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "60px 24px",
                    gap: 12,
                    color: "#6b7280",
                  }}
                >
                  <i
                    className="fas fa-spinner fa-spin"
                    style={{ fontSize: 28 }}
                    aria-hidden="true"
                  />
                  <div style={{ fontSize: 13 }}>Loading tickets...</div>
                </div>
              ) : !isLoading && posts.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "60px 24px",
                    gap: 12,
                    color: "#6b7280",
                    textAlign: "center",
                  }}
                >
                  <i
                    className="fas fa-inbox"
                    style={{ fontSize: 40, opacity: 0.35 }}
                    aria-hidden="true"
                  />
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>
                    No tickets yet
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {searchRef
                      ? `No tickets match "${searchRef}".`
                      : "You have no tickets assigned to you at the moment."}
                  </div>
                  {searchRef && (
                    <button
                      className="btn-light"
                      style={{ marginTop: 4 }}
                      onClick={() => {
                        setSearchRef("");
                        setPage(1);
                        fetchPosts(1);
                      }}
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
              <>
              {/* Top toolbar */}
              <div
                className="toolbar"
                style={{ marginBottom: 12 }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ position: "relative" }}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search with REF"
                      value={searchRef}
                      onChange={(e) => setSearchRef(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setPage(1);
                          fetchPosts(1);
                        }
                      }}
                      style={{
                        background: "#fff",
                        color: "#111827",
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        padding: "10px 38px 10px 12px",
                        width: 280,
                        outline: "none",
                      }}
                    />
                    <kbd style={{
                      position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                      fontFamily: "monospace", fontSize: 11, fontWeight: 700,
                      background: "#f3f4f6", border: "1px solid #e5e7eb", borderBottomWidth: 2,
                      borderRadius: 5, padding: "1px 5px", color: "#6b7280",
                      pointerEvents: "none", userSelect: "none",
                    }}>/</kbd>
                  </div>
                  <button
                    className="btn-light"
                    onClick={() => {
                      setPage(1);
                      fetchPosts(1);
                    }}
                  >
                    <i className="fas fa-search"></i>
                  </button>
                </div>

                {/* Numbered pager */}
                <div className="pager">
                  {/* First & Prev */}
                  <button
                    className="btn-ghost"
                    disabled={isLoading || page <= 1}
                    onClick={() => goToPage(1)}
                    title="First page"
                    aria-label="First page"
                  >
                    « First
                  </button>
                  <button
                    className="btn-ghost"
                    disabled={isLoading || page <= 1}
                    onClick={() => goToPage(page - 1)}
                    title="Previous page"
                    aria-label="Previous page"
                  >
                    ◀ Prev
                  </button>

                  {/* Numeric buttons if we know total pages */}
                  {totalPages ? (
                    <div
                      className="d-flex gap-12"
                      aria-label="Page numbers"
                    >
                      {pageNumbers.map((n, idx) =>
                        n === "…" ? (
                          <span
                            key={`gap-${idx}`}
                            style={{ padding: "0 6px" }}
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={`p-${n}`}
                            className={`page-btn ${page === n ? "active" : ""}`}
                            onClick={() => goToPage(n)}
                            disabled={isLoading}
                            aria-current={page === n ? "page" : undefined}
                            aria-label={`Page ${n}`}
                          >
                            {n}
                          </button>
                        ),
                      )}
                    </div>
                  ) : (
                    <span>Page {page}</span>
                  )}

                  {/* Next & Last */}
                  <button
                    className="btn-ghost"
                    disabled={isLoading || isLastPage}
                    onClick={() => goToPage(page + 1)}
                    title="Next page"
                    aria-label="Next page"
                  >
                    Next ▶
                  </button>
                  <button
                    className="btn-ghost"
                    disabled={isLoading || !totalPages || page >= totalPages}
                    onClick={() => goToPage(totalPages || page)}
                    title="Last page"
                    aria-label="Last page"
                  >
                    Last »
                  </button>
                </div>

                {(userType === "crmmanager" || userType === "superadmin") && (
                  <button
                    className="btn-light"
                    onClick={() => setShowCRMFilterModal(true)}
                  >
                    <i className="fas fa-filter"></i> Filter
                  </button>
                )}
              </div>

              {/* Cards */}
              <div className="card-grid">
                {(posts || []).map((row) => {
                  const lastStatus =
                    row.ticket?.status?.[row.ticket.status.length - 1]
                      ?.status || null;

                  const statusValue = lastStatus
                    ? statuses.find((s) => s.value === lastStatus) || null
                    : null;

                  const priorityValue = row.ticket?.priority
                    ? priorities.find((p) => p.value === row.ticket.priority) ||
                      null
                    : null;

                  const tagOptions = (tags || []).map((t) => ({
                    value: t,
                    label: t,
                  }));
                  const tagValues =
                    row.ticket?.tags?.map((t) => ({ value: t, label: t })) ||
                    [];

                  // Selected assignees for this row (filter what we *display* for brandadmin)
                  const assigneeValues = (row.ticket?.assignees || [])
                    .map((a) => {
                      const acct = accountById.get(a.user);
                      if (!acct) return null;
                      if (
                        isBrandAdmin &&
                        !allowedTypes.has(
                          String(acct.organizationType || "").toLowerCase(),
                        )
                      ) {
                        return null; // hide non-allowed selections from UI
                      }
                      return {
                        value: a.user,
                        label: `${acct.email} (${acct.organization})`,
                      };
                    })
                    .filter(Boolean);

                  const shortId = String(row._id).slice(-6);
                  const priorityMeta = priorities.find(
                    (p) => p.value === row.ticket?.priority,
                  );
                  const statusMeta = statusMetaFor(lastStatus);

                  return (
                    <article
                      key={row._id}
                      className="ticket-card"
                    >
                      {/* Header */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          marginBottom: 10,
                          alignItems: "center",
                        }}
                      >
                        {/* Clickable short-id with status dot + external-icon */}
                        <a
                          href={`/post/${row._id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pill pill-link"
                          title="Open post in a new tab"
                          aria-label={`Open post ${shortId} in a new tab`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          <Dot
                            color={statusMeta?.color}
                            mr={6}
                          />
                          <strong>#{shortId}</strong>
                          <i
                            className="fas fa-external-link-alt"
                            style={{
                              marginLeft: 8,
                              opacity: 0.8,
                              fontSize: 12,
                            }}
                            aria-hidden="true"
                          />
                        </a>

                        <span
                          className="pill"
                          title={fmtDate(row.dateTime)}
                        >
                          <i className="far fa-clock"></i>{" "}
                          {fmtDate(row.dateTime)}
                        </span>
                      </div>

                      {/* Meta row */}
                      <div
                        className="ticket-meta"
                        style={{ marginBottom: 12 }}
                      >
                        <div>
                          <strong>Category:</strong> {row.category || "—"}
                        </div>
                        <div>
                          <strong>City:</strong> {row.city || "—"}
                        </div>
                        <div>
                          <strong>Amount:</strong> {row.price || "—"}
                        </div>
                      </div>

                      {/* Description */}
                      <div
                        className="ticket-desc"
                        style={{ marginBottom: 12 }}
                      >
                        {row.description || "—"}
                      </div>

                      {/* Controls */}
                      <div
                        className="ticket-controls"
                        style={{ marginBottom: 12 }}
                      >
                        {/* Status (always shown) */}
                        <div>
                          <label style={{ fontSize: 12, color: "#374151" }}>
                            Status
                          </label>
                          <br />
                          {adminEnabled ? (
                            <Select
                              styles={lightSelectStyles}
                              options={statuses}
                              value={statusValue}
                              onChange={(opt) =>
                                opt && handleStatusAPI(opt.value, row._id)
                              }
                              placeholder="Select status"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              closeMenuOnScroll={false}
                              components={selectWithDots}
                            />
                          ) : (
                            <div className="pill">
                              <Dot
                                color={statusMeta?.color}
                                mr={6}
                              />
                              {lastStatus || "—"}
                            </div>
                          )}
                        </div>

                        {/* Priority */}
                        <div>
                          <label style={{ fontSize: 12, color: "#374151" }}>
                            Priority
                          </label>
                          <br />
                          {adminEnabled ? (
                            <Select
                              styles={lightSelectStyles}
                              options={priorities}
                              value={priorityValue}
                              onChange={(opt) =>
                                opt && handlePriorityAPI(opt.value, row._id)
                              }
                              placeholder="Select priority"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              closeMenuOnScroll={false}
                              components={selectWithDots}
                            />
                          ) : (
                            <div className="pill">
                              <Dot
                                color={priorityMeta?.color}
                                mr={6}
                              />
                              {row.ticket?.priority || "—"}
                            </div>
                          )}
                        </div>

                        {/* Tags */}
                        <div>
                          <label style={{ fontSize: 12, color: "#374151" }}>
                            Tags & Keywords
                          </label>
                          {adminEnabled ? (
                            <CreatableSelect
                              isMulti
                              styles={lightSelectStyles}
                              options={tagOptions}
                              value={tagValues}
                              placeholder="Add tags"
                              onChange={(opts) =>
                                handleTagsAPI(
                                  (opts || []).map((o) => o.value),
                                  row._id,
                                )
                              }
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              closeMenuOnScroll={false}
                            />
                          ) : row.ticket?.tags?.length ? (
                            <div style={{ marginTop: 6 }}>
                              {row.ticket.tags.map((t) => (
                                <span
                                  className="tag-pill"
                                  key={t}
                                >
                                  #{t}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div
                              className="pill"
                              style={{ marginTop: 6 }}
                            >
                              —
                            </div>
                          )}
                        </div>

                        {/* Assignees: show for admin users OR brandadmin.
                            Brandadmin: UI only shows enforcement/judiciary,
                            but PATCH preserves any previously-selected hidden assignees. */}
                        {(isAdminUser || isBrandAdmin) && (
                          <div>
                            <label style={{ fontSize: 12, color: "#374151" }}>
                              Escalate
                            </label>
                            <Select
                              isMulti
                              styles={lightSelectStyles}
                              options={escalateOptions}
                              value={assigneeValues}
                              placeholder="Escalate to..."
                              onChange={(opts) => {
                                const chosenIds = (opts || []).map(
                                  (o) => o.value,
                                );

                                if (isBrandAdmin && allowedAssigneeIdSet) {
                                  // Split existing selections into allowed vs hidden/disallowed
                                  const prevIds = (
                                    row.ticket?.assignees || []
                                  ).map((a) => a.user);
                                  const prevHidden = prevIds.filter(
                                    (id) => !allowedAssigneeIdSet.has(id),
                                  );
                                  const chosenAllowed = chosenIds.filter((id) =>
                                    allowedAssigneeIdSet.has(id),
                                  );

                                  // UNION: keep previous hidden + newly chosen allowed
                                  const combined = Array.from(
                                    new Set([...prevHidden, ...chosenAllowed]),
                                  );
                                  handleAssigneesAPI(combined, row._id);
                                } else {
                                  // Admin/superadmin: normal behavior (whatever is chosen)
                                  handleAssigneesAPI(chosenIds, row._id);
                                }
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              closeMenuOnScroll={false}
                            />
                          </div>
                        )}

                        {/* Remarks (admins only) */}
                        {adminEnabled && (
                          <div>
                            <label style={{ fontSize: 12, color: "#374151" }}>
                              Internal Remarks
                            </label>
                            <br />
                            <button
                              className="btn-light"
                              onClick={() => {
                                setCurrentPost(row._id);
                                setCurrentRemarks(row.ticket?.remarks || []);
                                setShowRemarksFormModal(true);
                              }}
                            >
                              <i className="fas fa-comment" />{" "}
                              {row.ticket?.remarks?.length || 0}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Documents */}
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                          }}
                        >
                          {row.ticket && Object.keys(row.ticket).length > 0 && (
                            <label
                              className="btn-light"
                              style={{
                                borderRadius: 20,
                                padding: "6px 12px",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <i className="fas fa-paperclip" /> Attach
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const file =
                                    e.target.files && e.target.files[0];
                                  if (file) handleUploadAPI(file, row._id);
                                }}
                              />
                            </label>
                          )}
                        </div>

                        {row?.ticket?.documents?.length ? (
                          <div className="doc-grid">
                            {row.ticket.documents.map((doc) => {
                              const fileUrl = doc?.url || "";
                              const fileName =
                                (doc?.filename || "").split("/").pop() ||
                                "file";
                              const display =
                                fileName.length > 20
                                  ? fileName.slice(0, 20) + "..."
                                  : fileName;

                              const uploadedBy = doc.user;

                              const canDelete =
                                String(uploadedBy || "") === String(userId) ||
                                userType === "superadmin" ||
                                userType === "crmmanager";

                              return (
                                <div
                                  className="doc-card"
                                  key={doc._id}
                                  style={{ justifyContent: "space-between" }}
                                >
                                  <div
                                    className="doc-icon"
                                    aria-hidden="true"
                                  >
                                    <i className="fas fa-file-alt" />
                                  </div>

                                  <a
                                    href={fileUrl}
                                    title={fileName}
                                    download={fileName}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleDocDownload(fileUrl, fileName);
                                    }}
                                    style={{ flex: 1 }}
                                  >
                                    <div className="doc-name">{display}</div>
                                  </a>
                                  <div
                                    className="doc-actions"
                                    style={{ marginLeft: "auto" }}
                                  >
                                    {canDelete && (
                                      <button
                                        onClick={() => {
                                          setDeleteDocTarget({
                                            postId: row._id,
                                            docId: doc._id,
                                            filename: (doc?.filename || "").split("/").pop() || "file",
                                          });
                                          setShowDeleteDocModal(true);
                                        }}
                                        className="btn-icon"
                                        title="Delete"
                                        aria-label="Delete document"
                                      >
                                        <i className="fas fa-trash-alt" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p
                            style={{
                              color: "#6b7280",
                              fontStyle: "italic",
                              margin: 0,
                            }}
                          >
                            No documents yet
                          </p>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Bottom pager */}
              <div
                className="pager"
                style={{
                  marginTop: 14,
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                {/* Totals if available */}
                <div style={{ color: "#374151", fontSize: 12 }}>
                  {totalPosts != null ? (
                    <>
                      Total: {totalPosts.toLocaleString()} tickets • Page {page}
                      {totalPages ? ` of ${totalPages}` : ""}
                    </>
                  ) : (
                    <>Page {page}</>
                  )}
                </div>

                <div className="pager">
                  <button
                    className="btn-ghost"
                    disabled={isLoading || page <= 1}
                    onClick={() => goToPage(1)}
                    title="First page"
                    aria-label="First page"
                  >
                    « First
                  </button>
                  <button
                    className="btn-ghost"
                    disabled={isLoading || page <= 1}
                    onClick={() => goToPage(page - 1)}
                    title="Previous page"
                    aria-label="Previous page"
                  >
                    ◀ Prev
                  </button>

                  {totalPages ? (
                    <div
                      className="d-flex gap-12"
                      aria-label="Page numbers"
                    >
                      {pageNumbers.map((n, idx) =>
                        n === "…" ? (
                          <span
                            key={`gapb-${idx}`}
                            style={{ padding: "0 6px" }}
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={`pb-${n}`}
                            className={`page-btn ${page === n ? "active" : ""}`}
                            onClick={() => goToPage(n)}
                            disabled={isLoading}
                            aria-current={page === n ? "page" : undefined}
                            aria-label={`Page ${n}`}
                          >
                            {n}
                          </button>
                        ),
                      )}
                    </div>
                  ) : (
                    <span>Page {page}</span>
                  )}

                  <button
                    className="btn-ghost"
                    disabled={isLoading || isLastPage}
                    onClick={() => goToPage(page + 1)}
                    title="Next page"
                    aria-label="Next page"
                  >
                    Next ▶
                  </button>
                  <button
                    className="btn-ghost"
                    disabled={isLoading || !totalPages || page >= totalPages}
                    onClick={() => goToPage(totalPages || page)}
                    title="Last page"
                    aria-label="Last page"
                  >
                    Last »
                  </button>
                </div>
              </div>
              </>
              )}
            </div>
          </section>
        </div>
      </div>

      <RemarksForm
        domain={domain}
        show={showRemarksFormModal}
        remarks={currentRemarks}
        handleClose={() => setShowRemarksFormModal(false)}
        handleFormSubmit={(val) => {
          if (!userDataState) {
            navigate("/signin");
            return;
          }
          handleRemarksAPI(currentPost, val)
            .then(() => setShowRemarksFormModal(false))
            .catch(console.error);
        }}
      />

      <CRMFilterModal
        show={showCRMFilterModal}
        handleClose={() => setShowCRMFilterModal(false)}
        handleFormSubmit={(val) => alert("Filter submitted: " + val)}
      />

      <DeleteDocumentModal
        show={showDeleteDocModal}
        handleClose={() => setShowDeleteDocModal(false)}
        filename={deleteDocTarget?.filename}
        onConfirm={() => handleDeleteDocAPI(deleteDocTarget.postId, deleteDocTarget.docId)}
      />
    </>
  );
};

export default Dashboard;
